/* eslint-disable no-undef */
/* eslint-disable max-len */
/* eslint-disable no-unused-vars */

import { PolymerElement } from "@polymer/polymer/polymer-element.js";
import { html } from "@polymer/polymer/lib/utils/html-tag.js";
import template from "./ns-region.html";
import { events } from "@nuskin/ns-util";

/**
 * The ns-region should be used for
 *
 * <h1>Region</h1>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-region>Sample Button</ns-region>
 *   </template>
 * </demo-snippet>
 *
 */
export class NsRegion extends PolymerElement {
  static get is() {
    return "ns-region";
  }

  static get template() {
    let htmlElement = document.createElement("TEMPLATE");
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  static get properties() {
    return {
      region: {
        type: Object
      },
      active: {
        type: String,
        value: "inactive"
      },
      activeLanguage: {
        type: String,
        value: "inactive"
      },
      localeIndex: {
        type: Object,
        notify: false,
        value: {
          region: 0,
          country: 0,
          language: 0
        }
      },
      lock: {
        type: Boolean,
        value: false
      },
      location: {
        type: String,
        value: "footer"
      },
      countryTranslationOverrides: {
        type: Array,
        value: []
      },
      currentLanguageCode: {
        type: String,
        value: "en"
      },
      regionLabelOverrides: {
        type: Array,
        value: []
      },
      /**
       * Overrides the url the user is redirected to.
       * Region, country, and language can be dynamically set by using
       * the tags {region}, {country}, and {language}.
       * Example: "/content/mecommerce/{region}/{country}/{language}/home.html"
       */
      urlPattern: {
        type: String,
        value: ""
      }
    };
  }

  ready() {
    super.ready();

    // Workaround for browsers that have no support for :host-context yet
    if (window.location.href.indexOf("iw_IL") > 0) {
      let css = ".region-row h3 { padding-right: 0px; padding-left: 20px;} ";
      css += ".languages-row a {padding-right: 0px; padding-left: 20px;} ";
      css += ".languages-row a::after {display: none;} ";
      css +=
        '.languages-row a::before {content: "|"; position: absolute; left: 8px; color: #D1D1D1;} ';
      css += ".languages-row a:last-of-type::before {display: none;} ";

      this.shadowRoot
        .querySelector("style")
        .appendChild(document.createTextNode(css));
    }

    if (this.active === "active") {
      this.$.regionCollapse.show();
    } else {
      this.$.regionCollapse.hide();
    }
  }

  toggleContainer() {
    if (this.active === "active" && !this.lock) {
      this.active = "inactive";
      this.$.regionCollapse.hide();
    } else {
      this.active = "active";
      this.$.regionCollapse.show();
    }
  }

  _hideCountryFlag(countryCode) {
    switch (countryCode) {
      case "TW":
      case "HK":
        return true;
    }

    return false;
  }

  _getRegionLabel(regionName, defaultLabel, currentLanguageCode) {
    for (let i = 0; i < this.regionLabelOverrides.length; i++) {
      let regionLabelOverride = JSON.parse(this.regionLabelOverrides[i]);
      if (regionName === regionLabelOverride.regionName) {
        if (regionLabelOverride.languageCode === currentLanguageCode) {
          return regionLabelOverride.translatedValue;
        }
      }
    }

    return defaultLabel;
  }

  _getLocaleCountryName(country, languageCode) {
    //Check first if country has override value
    if (this.countryTranslationOverrides) {
      for (let i = 0; i < this.countryTranslationOverrides.length; i++) {
        let override = JSON.parse(this.countryTranslationOverrides[i]);
        if (
          override.hasOwnProperty("countryCode") &&
          override.countryCode.toUpperCase() ===
            country.countryCode.toUpperCase()
        ) {
          if (
            override.hasOwnProperty("languageCode") &&
            override.languageCode.toLowerCase() === languageCode.toLowerCase()
          ) {
            if (override.hasOwnProperty("translatedValue")) {
              return override.translatedValue;
            }
          }
        }
      }
    }

    // If not override get name from service
    for (let i = 0; i < country.locale_names.length; i++) {
      if (country.locale_names[i].hasOwnProperty(languageCode)) {
        return country.locale_names[i][languageCode];
      }
    }
  }

  sortCountriesAlphabetically(a, b) {
    if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
    if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
    return 0;
  }

  _setLocale(e) {
    e.preventDefault();
    let element = e.target;
    let languageIndex = element.getAttribute("data-language-index");
    let countryIndex;
    let regionIndex;
    while (!countryIndex && element) {
      element = element.parentNode;
      if (element && element.getAttribute("data-country-index")) {
        countryIndex = element.getAttribute("data-country-index");
      }
    }
    while (!regionIndex && element) {
      element = element.parentNode;
      if (element && element.getAttribute("data-region-index")) {
        regionIndex = element.getAttribute("data-region-index");
      }
    }

    this.localeIndex = {
      region: regionIndex,
      country: countryIndex,
      language: languageIndex
    };
    localStorage.setItem(
      "localeIndex",
      regionIndex + "," + countryIndex + "," + languageIndex
    );
    let href = e.target.getAttribute("href");
    events.publish("regionSelect", href);
    window.location.href = href;
  }

  _setMarketLink(languageIndex, country) {
    if (this.urlPattern) {
      return (
        this.baseUrl +
        this.urlPattern
          .replace(/{region}/g, this.region)
          .replace(/{country}/g, country.countryCode)
          .replace(/{language}/g, country.languageCodes[languageIndex])
      );
    } else if (this.baseUrl.indexOf("mynuskin") !== -1) {
      return (
        this.baseUrl +
        "/content/mecommerce/" +
        this.region +
        "/" +
        country.countryCode +
        "/" +
        country.languageCodes[languageIndex] +
        "/home.html"
      );
    } else {
      if (country.countryCode === "KR") {
        return this._findCountryLanguagePath(
          country,
          country.languageCodes[languageIndex]
        ) + "/";
      } else {
        return (
          this._findCountryLanguagePath(
            country,
            country.languageCodes[languageIndex]
          ) + "/home.html"
        );
      }
    }
  }

  _findCountryLanguagePath(country, language) {
    for (let i = 0; i < this.region.countryname.length; i++) {
      if (this.region.countryname[i].countryCode === country.countryCode) {
        for (let n = 0; n < this.region.countryname[i].paths.length; n++) {
          if (this.region.countryname[i].paths[n].hasOwnProperty(language)) {
            return this.region.countryname[i].paths[n][language];
          }
        }
      }
    }
  }
}

customElements.define(NsRegion.is, NsRegion);
